import { assert, assertNever, defined, definedMap } from "@glideapps/ts-necessities";
import { parseValueAsDateSync } from "common/components/component-helpers";
import { compareValues } from "common/components/primitives";
import type { AppFacilities, ComputationModel } from "common/components/types";
import { ActionSource } from "common/components/types";
import { computeCondition } from "common/computation-model/conditions";
import type { Row, Table } from "common/computation-model/data";
import { asRow, asTable } from "common/computation-model/data";
import { isRootPath } from "common/computation-model/paths";
import type { ConditionValuePreparer } from "common/computation-model/prepare-condition";
import { prepareCondition } from "common/computation-model/prepare-condition";
import type { TableName } from "common/database-strings";
import type {
    ContextTableTypes,
    ExistingAppDescriptionContext,
    WireInflationBackend,
    WirePredicate,
    WireRowComponentInflationBackend,
    WireRowHydrationValueProvider,
    WireTableGetter,
    WireValueGetter,
} from "common/generator/components";
import {
    getEnumProperty,
    getNumberProperty,
    getSourceColumnProperty,
    getSpecialValueProperty,
    getStringProperty,
    getSwitchProperty,
    getTableProperty,
} from "common/generator/components/property-utils";
import type {
    ArrayTransform,
    ColumnType,
    FilterArrayTransform,
    Formula,
    LegacyPropertyDescription,
    LimitArrayTransform,
    MutatingScreenKind,
    ShuffleArrayTransform,
    SortArrayTransform,
    SourceColumn,
    TableGlideType,
    TableOrderArrayTransform,
} from "common/generator/description";
import {
    ArrayTransformKind,
    getSourceColumnPath,
    getTableColumn,
    getTableName,
    isMultiRelationType,
    makeInputOutputTables,
    SortOrder,
    SourceColumnKind,
    SpecialValueKind,
} from "common/generator/description";
import {
    decomposePredicateCombinationFormula,
    decomposeSortKey,
} from "common/generator/formulas/formula-specifications";
import { findType, nullToUndefined } from "common/Support";
import shuffle from "lodash/shuffle";
import { v4 as uuid } from "uuid";
import type { TableTransformer } from "./internal-types";

const unboundValueGetter: WireValueGetter = () => null;
const unboundColumnGetter = [unboundValueGetter, { kind: "string" }] as const;

export class InflationBackend implements WireRowComponentInflationBackend {
    constructor(
        public readonly appFacilities: AppFacilities,
        public readonly adc: ExistingAppDescriptionContext,
        public readonly tables: ContextTableTypes,
        private readonly computationModel: ComputationModel,
        public readonly mutatingScreenKind: MutatingScreenKind | undefined
    ) {}

    private makeColumnGetter(
        sourceColumn: SourceColumn,
        inOutputRow: boolean,
        withFormat: boolean,
        tables: ContextTableTypes = this.tables
    ): readonly [WireValueGetter, ColumnType] {
        const sourceColumnPath = getSourceColumnPath(sourceColumn);
        assert(sourceColumnPath.length === 1);
        const columnName = sourceColumnPath[0];

        let table: TableGlideType;
        let rowGetter: (hb: WireRowHydrationValueProvider) => Row | undefined;

        if (sourceColumn.kind === SourceColumnKind.DefaultContext) {
            table = inOutputRow ? tables.output : tables.input;

            rowGetter = hb => {
                const sc = hb.rowContext;
                return inOutputRow ? sc?.outputRow ?? sc?.inputRow : sc?.inputRow;
            };
        } else if (sourceColumn.kind === SourceColumnKind.UserProfile) {
            assert(!inOutputRow);

            const info = this.adc.userProfileTableInfo;
            if (info === undefined) return unboundColumnGetter;

            const tableName = info.tableName;
            const maybeTable = this.adc.findTable(tableName);
            if (maybeTable === undefined) return unboundColumnGetter;
            table = maybeTable;

            const userProfilePath = this.computationModel.getUserProfileRowPath();
            if (userProfilePath === undefined) return unboundColumnGetter;

            rowGetter = hb => definedMap(hb.getGlobalValue(tableName, userProfilePath), asRow);
        } else if (sourceColumn.kind === SourceColumnKind.ContainingScreen) {
            if (tables.containingScreen === undefined) return unboundColumnGetter;
            table = tables.containingScreen;

            rowGetter = hb => hb.rowContext?.containingScreenRow;
        } else {
            return assertNever(sourceColumn.kind);
        }

        const column = getTableColumn(table, columnName);

        const tableName = getTableName(table);
        const paths = this.computationModel.getColumnPaths(tableName)?.get(columnName);

        if (column === undefined || paths === undefined) return unboundColumnGetter;

        const [[valuePath, valueRootPath], [formatPath, formatRootPath]] = paths;
        const path = withFormat ? formatPath ?? valuePath : valuePath;
        const rootPath = withFormat ? formatRootPath ?? valueRootPath : valueRootPath;

        let getter: WireValueGetter;
        if (isRootPath(path)) {
            getter = hb => hb.getGlobalValue(tableName, path);
        } else {
            getter = hb => {
                if (rootPath === undefined) return undefined;

                const row = rowGetter(hb);
                if (row === undefined) return undefined;

                return hb.getColumnInRow(tableName, rootPath, row, path);
            };
        }
        return [getter, column.type];
    }

    public getValueGetterForColumn(
        sourceColumn: SourceColumn,
        inOutputRow: boolean,
        withFormat: boolean
    ): readonly [WireValueGetter, ColumnType] {
        return this.makeColumnGetter(sourceColumn, inOutputRow, withFormat);
    }

    private makeTableGetter(
        tableName: TableName
    ): ((hb: WireRowHydrationValueProvider) => Table | undefined) | undefined {
        const table = this.adc.findTable(tableName);
        if (table === undefined) return undefined;

        const rootPath = this.computationModel.getBasePathForTable(tableName);
        if (rootPath === undefined) return undefined;

        return hb => definedMap(hb.getGlobalValue(tableName, rootPath), t => asTable(t));
    }

    // TODO: This should be in the description handlers for the most part.  Maybe.
    public getValueGetterForProperty(
        desc: LegacyPropertyDescription | undefined,
        withFormat: boolean,
        opts?: {
            inOutputRow?: boolean;
            columnFirst?: boolean;
        }
    ): readonly [WireValueGetter, ColumnType] {
        const inOutputRow = opts?.inOutputRow ?? false;
        const columnFirst = opts?.columnFirst ?? true;

        const columnValue = getSourceColumnProperty(desc);
        if (columnFirst && columnValue !== undefined) {
            return this.makeColumnGetter(columnValue, inOutputRow, withFormat);
        }

        const stringValue = getStringProperty(desc);
        if (stringValue !== undefined) {
            if (stringValue === "") return unboundColumnGetter;
            return [() => stringValue, { kind: "string" }];
        }

        if (columnValue !== undefined) {
            return this.makeColumnGetter(columnValue, inOutputRow, withFormat);
        }

        const switchValue = getSwitchProperty(desc);
        if (switchValue !== undefined) {
            return [() => switchValue, { kind: "boolean" }];
        }

        const numberValue = getNumberProperty(desc);
        if (numberValue !== undefined) {
            return [() => numberValue, { kind: "number" }];
        }

        const enumValue = getEnumProperty(desc);
        if (enumValue !== undefined) {
            // We only support string enums for now.  This is trivial to fix, but
            // it should return the correct column type.
            assert(typeof enumValue === "string");
            return [() => enumValue, { kind: "string" }];
        }

        const specialValueProperty = getSpecialValueProperty(desc);
        if (specialValueProperty !== undefined) {
            switch (specialValueProperty) {
                case SpecialValueKind.Timestamp:
                    return [
                        hb => hb.getGlobalValue(undefined, this.computationModel.getTimestampPath()),
                        { kind: "date-time" },
                    ];
                case SpecialValueKind.VerifiedEmailAddress:
                    return [
                        hb => hb.getGlobalValue(undefined, this.computationModel.getVerifiedEmailAddressPath()),
                        { kind: "email-address" },
                    ];
                case SpecialValueKind.RealEmailAddress:
                    return [
                        hb => hb.getGlobalValue(undefined, this.computationModel.getRealEmailAddressPath()),
                        { kind: "email-address" },
                    ];
                case SpecialValueKind.UniqueIdentifier:
                    return [() => uuid(), { kind: "string" }];
                case SpecialValueKind.ActionSource:
                    return [() => ActionSource.None, { kind: "string" }];
                case SpecialValueKind.CurrentURL:
                    // We're not subscribing to this.  Right now we only use
                    // this special value in actions, so it's fine if it
                    // doesn't update when it changes.
                    return [() => window.location.href, { kind: "uri" }];
                case SpecialValueKind.ClearColumn:
                    return [() => "", { kind: "string" }];
                default:
                    return assertNever(specialValueProperty);
            }
        }

        return [unboundValueGetter, { kind: "string" }];
    }

    public inflatePredicate(
        predicate: Formula,
        inOutputRow: boolean,
        tableTypeOverride?: TableGlideType
    ): WirePredicate | undefined {
        const condition = decomposePredicateCombinationFormula(predicate)?.spec;
        if (condition === undefined) return undefined;

        const tables: ContextTableTypes =
            definedMap(tableTypeOverride, t => ({
                input: t,
                output: t,
                containingScreen: this.tables.input,
            })) ?? this.tables;
        const preparer: ConditionValuePreparer<WireValueGetter> = {
            makePathForSourceColumn: sc => this.makeColumnGetter(sc, inOutputRow, false, tables)[0],
            getVerifiedEmailAddressPath: () => hb =>
                hb.getGlobalValue(undefined, this.computationModel.getVerifiedEmailAddressPath()),
            getTimestampPath: () => hb => hb.getGlobalValue(undefined, this.computationModel.getTimestampPath()),
            getStartOrEndOfTodayPath: startOrEnd => hb =>
                hb.getGlobalValue(undefined, this.computationModel.getStartOrEndOfTodayPath(startOrEnd)),
        };
        const conditions = condition.predicates.map(p => prepareCondition(p, preparer));
        return hb => {
            for (const c of conditions) {
                const result =
                    computeCondition(c, getter => nullToUndefined(getter(hb)), parseValueAsDateSync) ?? false;
                if (result && condition.combinator === "or") {
                    return true;
                } else if (!result && condition.combinator === "and") {
                    return false;
                }
            }
            return condition.combinator === "or" ? false : true;
        };
    }

    public inflateFilters(
        filters: readonly FilterArrayTransform[],
        inOutputRow: boolean,
        tableTypeOverride: TableGlideType | undefined
    ): WirePredicate {
        const predicates: WirePredicate[] = [];

        for (const filterTransform of filters) {
            const predicate = this.inflatePredicate(filterTransform.predicate, inOutputRow, tableTypeOverride);
            if (predicate === undefined) continue;

            predicates.push(predicate);
        }

        return hb => {
            for (const p of predicates) {
                if (!p(hb)) {
                    return false;
                }
            }
            return true;
        };
    }

    public inflateTransforms(tableType: TableGlideType, transforms: readonly ArrayTransform[]): TableTransformer {
        const reverseTransform = findType(
            transforms,
            (t): t is TableOrderArrayTransform => t.kind === ArrayTransformKind.TableOrder && t.reverse
        );
        const shuffleTransform = findType(
            transforms,
            (t): t is ShuffleArrayTransform => t.kind === ArrayTransformKind.Shuffle
        );
        const limitTransform = findType(
            transforms,
            (t): t is LimitArrayTransform => t.kind === ArrayTransformKind.Limit
        );

        const filterTransform = findType(
            transforms,
            (t): t is FilterArrayTransform => t.kind === ArrayTransformKind.Filter
        );
        let filterPredicate: ((hb: WireRowHydrationValueProvider) => boolean) | undefined;
        if (filterTransform !== undefined) {
            filterPredicate = this.inflateFilters([filterTransform], false, tableType);
        }

        const sortTransform = findType(transforms, (t): t is SortArrayTransform => t.kind === ArrayTransformKind.Sort);
        let sortGetterAndReverse: [WireValueGetter, boolean] | undefined;
        if (sortTransform !== undefined && sortTransform.keys.length > 0) {
            assert(sortTransform.keys.length === 1);
            const { key, order } = sortTransform.keys[0];
            const columnName = decomposeSortKey(key);
            if (columnName !== undefined) {
                const [keyGetter] = this.makeColumnGetter(
                    { kind: SourceColumnKind.DefaultContext, name: [columnName] },
                    false,
                    false,
                    { ...makeInputOutputTables(tableType), containingScreen: undefined }
                );
                sortGetterAndReverse = [keyGetter, order === SortOrder.Descending];
            }
        }

        return (rvpm, table) => {
            let rows = Array.from(table.values()).filter(r => r.$isVisible);

            if (filterPredicate !== undefined) {
                // NOTE: We could be much smarter here.  If there's no sort,
                // then we can just stop gathering rows until we've fulfilled
                // our limit, or however many rows we need to display in the
                // component.
                rows = rows.filter(r => {
                    // TODO: If we have filter and sort we're making value
                    // providers twice by row.
                    const rhb = rvpm.makeRowValueProvider(r);
                    return defined(filterPredicate)(rhb);
                });
            }

            if (sortGetterAndReverse !== undefined) {
                const [keyGetter, reverse] = sortGetterAndReverse;
                const rowsWithKeys = rows.map(r => {
                    const rhb = rvpm.makeRowValueProvider(r);
                    return [keyGetter(rhb), r] as const;
                });

                // ##sorting:
                // These two are copies and must be kept in sync.
                rows = rowsWithKeys
                    .sort(([one], [two]) => {
                        if (typeof one === "string") {
                            one = one.trim().toLowerCase();
                        }
                        if (typeof two === "string") {
                            two = two.trim().toLowerCase();
                        }
                        const result = compareValues(one, two);
                        if (result !== undefined) {
                            return result;
                        }
                        if (one === undefined) {
                            return -1;
                        } else {
                            return 1;
                        }
                    })
                    .map(([, r]) => r);

                if (reverse) {
                    rows = rows.reverse();
                }
            } else if (reverseTransform !== undefined) {
                rows = rows.reverse();
            } else if (shuffleTransform !== undefined) {
                rows = shuffle(rows);
            }
            if (rows === undefined) {
                rows = Array.from(table.values());
            }

            if (limitTransform !== undefined) {
                const numRows = getNumberProperty(limitTransform.numRows);
                if (numRows !== undefined && rows.length > numRows) {
                    rows = rows.slice(0, numRows);
                }
            }

            return rows;
        };
    }

    public getValueGetterForTableComponent(
        desc: LegacyPropertyDescription,
        transforms: readonly ArrayTransform[]
    ): [WireTableGetter, WireInflationBackend] | undefined {
        let getter: (hb: WireRowHydrationValueProvider) => Table | null | undefined;
        let tableType: TableGlideType | undefined;

        const columnName = getSourceColumnProperty(desc);
        if (columnName !== undefined) {
            const [valueGetter, columnType] = this.makeColumnGetter(columnName, false, false);

            if (!isMultiRelationType(columnType)) return undefined;
            tableType = this.adc.findTable(columnType.items);
            if (tableType === undefined) return undefined;

            getter = hb => definedMap(valueGetter(hb), v => (v === null ? null : asTable(v)));
        } else {
            const tableName = getTableProperty(desc);
            if (tableName === undefined) return undefined;

            tableType = this.adc.findTable(tableName);

            const maybeGetter = this.makeTableGetter(tableName);
            if (maybeGetter === undefined) return undefined;
            getter = maybeGetter;
        }

        if (getter === undefined || tableType === undefined) return undefined;

        const ib = new InflationBackend(
            this.appFacilities,
            this.adc,
            { ...makeInputOutputTables(tableType), containingScreen: this.tables.input },
            this.computationModel,
            this.mutatingScreenKind
        );

        const transformTable = this.inflateTransforms(tableType, transforms);

        const transformedGetter: WireTableGetter = hb => {
            const table = defined(getter)(hb);
            if (table === undefined || table === null) return undefined;

            const ttvp = hb.makeTableTransformValueProvider(defined(tableType));
            if (ttvp === undefined) return undefined;

            const rows = transformTable(ttvp, table);

            return ttvp.makeHydrationBackendForTable(rows);
        };

        return [transformedGetter, ib];
    }

    public makeInflationBackendForTables(tables: ContextTableTypes): InflationBackend {
        return new InflationBackend(
            this.appFacilities,
            this.adc,
            tables,
            this.computationModel,
            this.mutatingScreenKind
        );
    }

    public makeInflationBackendForOutputTable(): InflationBackend {
        return this.makeInflationBackendForTables({
            ...makeInputOutputTables(this.tables.output),
            containingScreen: this.tables.containingScreen,
        });
    }
}
